import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Pressable, Platform, ScrollView } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { getAccessibleModules } from '../../../../api/ticketApi';
import { createApiClient } from '../../../../api/apiClient';
import AdminModal from '../../components/AdminModal';
import AdminButton from '../../components/AdminButton';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import UserFormSelect from './UserFormSelect';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import AccessRequestLocationPicker, {
  LocationPickerType,
  LocationRegion,
} from '../../../TicketingPortal/components/AccessRequestLocationPicker';
import { ACCESS_TYPE_TIERS, mapScopeTypeToRoleScope, buildScopeConfigFromType } from '../../../TicketingPortal/components/SubmitTicketFormPanel';
import useSupportTierRoles from './hooks/useSupportTierRoles';

interface UserAddModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit?: (payload: UserAddPayload) => void | boolean | Promise<void | boolean>;
}

// Support-tier role names are fetched dynamically (useSupportTierRoles), so this can't be a fixed union.
type AccessType = string;
type FieldKey = 'firstName' | 'lastName' | 'email' | 'gpid' | 'organization' | 'department' | 'role' | 'locations';

interface AccessibleModuleItem {
  accessId: number;
  roleId: number;
  moduleId: number;
  moduleName: string;
  hasAccess: boolean;
  readOnly?: boolean;
}

type EmployeeAutocompleteResult = {
  // Fields returned by /admin/user-basic-access/search
  userEmail?: string | null;
  gpid?: string | null;
  status?: boolean | null;
  team?: string | null;
  // Legacy / fallback fields kept for compatibility
  gpId?: string | null;
  gp_id?: string | null;
  id?: string | null;
  employeeId?: string | null;
  name?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  givenName?: string | null;
  surname?: string | null;
  email?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
  user_principal_name?: string | null;
};

export interface UserAddPayload {
  accessType: AccessType;
  firstName: string;
  lastName: string;
  email: string;
  gpid: string;
  organization: string;
  department: string;
  role: string;
  roleId: number | null;
  selectedLocations: string[];
}

const DEPARTMENT_OPTIONS: Record<string, string[]> = {
  PBNA: ['Warehouse', 'Manufacturing', 'Delivery', 'Warehouse Delivered'],
  FLNA: ['Warehouse Delivered', 'Manufacturing', 'Delivery'],
  '1NA': ['Warehouse', 'Delivery'],
};

type DepartmentRoleOption = {
  roleId: number;
  roleName: string;
  scopeType?: string;
};

const normalizeRoleName = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

// Derives scope (full/region/market/site) + labels from the scopeType returned by
// /admin/users/roles for the given role name — no hardcoded per-role-name map.
const resolveRoleScope = (roleName: string, roles: DepartmentRoleOption[]) => {
  const normalized = normalizeRoleName(roleName);
  const match = roles.find((role) => normalizeRoleName(role.roleName) === normalized);
  const scopeType = match ? mapScopeTypeToRoleScope(match.scopeType) : null;
  return scopeType ? buildScopeConfigFromType(scopeType) : null;
};

const extractLocationRows = (response: any): Array<any> => {
  const root = response?.data ?? response;
  return Array.isArray(root)
    ? root
    : Array.isArray(root?.content)
    ? root.content
    : Array.isArray(root?.data?.content)
    ? root.data.content
    : Array.isArray(root?.data)
    ? root.data
    : [];
};

const initialForm: UserAddPayload = {
  accessType: 'Normal User',
  firstName: '',
  lastName: '',
  email: '',
  gpid: '',
  organization: '',
  department: '',
  role: '',
  roleId: null,
  selectedLocations: [],
};

const UserAddModal: React.FC<UserAddModalProps> = ({ visible, onClose, onSubmit }) => {
  const [form, setForm] = useState<UserAddPayload>(initialForm);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [accessibleModules, setAccessibleModules] = useState<AccessibleModuleItem[]>([]);
  const [accessibleLoading, setAccessibleLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [fetchedLocationTree, setFetchedLocationTree] = useState<LocationRegion[]>([]);
  const [roleOptionsData, setRoleOptionsData] = useState<DepartmentRoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuggestionsLoading, setEmailSuggestionsLoading] = useState(false);
  const [emailValidationLoading, setEmailValidationLoading] = useState(false);
  const [emailOptions, setEmailOptions] = useState<EmployeeAutocompleteResult[]>([]);
  const [selectedEmailOption, setSelectedEmailOption] = useState<EmployeeAutocompleteResult | null>(null);
  const [showEmailResults, setShowEmailResults] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formScrollRef = useRef<ScrollView | null>(null);
  const fieldPositionsRef = useRef<Partial<Record<FieldKey, number>>>({});

  const departmentOptions = useMemo(() => {
    if (!form.organization) return [];
    return DEPARTMENT_OPTIONS[form.organization] || [];
  }, [form.organization]);

  const roleOptions = useMemo(() => roleOptionsData.map((r) => r.roleName), [roleOptionsData]);

  const roleScope = useMemo(() => {
    if (!form.role) return null;
    return resolveRoleScope(form.role, roleOptionsData);
  }, [form.role, roleOptionsData]);

  const [accessTypeTier, setAccessTypeTier] = useState<typeof ACCESS_TYPE_TIERS[number]>('Field');
  const { supportTierRoles, supportTierRolesLoading } = useSupportTierRoles('user-management');
  // Any Support-tier role (dynamically fetched) is full-access — don't hardcode specific role names.
  const showFullAccess = accessTypeTier === 'Support';
  // Support roles aren't in a department roles list, so roleScope is null for them — treat as global.
  const isFullScope = roleScope?.type === 'full' || showFullAccess;

  const locationTree = fetchedLocationTree;

  const activeLocationTree = locationTree;

  const allSiteSelections = useMemo(
    () => Array.from(new Set(activeLocationTree.flatMap((r) => r.markets.flatMap((m) => m.sites.map((s) => s.name))))),
    [activeLocationTree],
  );

  const totalMarketCount = useMemo(
    () => activeLocationTree.reduce((sum, region) => sum + region.markets.length, 0),
    [activeLocationTree],
  );

  const pickerType = useMemo<LocationPickerType>(() => {
    if (!roleScope || roleScope.type === 'full') return 'site';
    return roleScope.type;
  }, [roleScope]);

  const visibleAccessibleModules = useMemo(() => {
    const seen = new Set<string>();
    return accessibleModules.filter((mod) => {
      if (!mod.hasAccess) return false;
      const key = `${mod.moduleId}:${mod.moduleName.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [accessibleModules]);

  // readOnly is missing on some records — default to read-only (view-only), matching the
  // Access Request form's split so the two screens show identical module access per role.
  const fullAccessModules = useMemo(
    () => visibleAccessibleModules.filter((mod) => mod.readOnly === false),
    [visibleAccessibleModules]
  );
  const viewOnlyModules = useMemo(
    () => visibleAccessibleModules.filter((mod) => mod.readOnly !== false),
    [visibleAccessibleModules]
  );

  const getInitials = (name: string) =>
    name.split(' ').slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase();

  const extractAutocompleteOptions = (payload: any): EmployeeAutocompleteResult[] => {
    const root = payload?.data ?? payload;
    const source =
      root?.data?.processedData ??
      root?.processedData ??
      root?.data?.content ??
      root?.content ??
      root?.data ??
      root;

    return Array.isArray(source) ? (source as EmployeeAutocompleteResult[]) : [];
  };

  const getCandidateEmail = (candidate?: EmployeeAutocompleteResult | null) =>
    String(candidate?.userEmail ?? candidate?.email ?? candidate?.mail ?? candidate?.userPrincipalName ?? candidate?.user_principal_name ?? '')
      .trim()
      .toLowerCase();

  const formatCandidateMeta = (candidate?: EmployeeAutocompleteResult | null) => {
    const gpid = String(
      candidate?.gpid ?? candidate?.gpId ?? candidate?.gp_id ?? candidate?.employeeId ?? candidate?.id ?? '-',
    ).trim() || '-';
    const email = String(
      candidate?.userEmail ?? candidate?.email ?? candidate?.mail ?? candidate?.userPrincipalName ?? candidate?.user_principal_name ?? '-',
    ).trim() || '-';
    return `GPID ${gpid} · ${email}`;
  };

  const getCandidateName = (candidate?: EmployeeAutocompleteResult | null) => {
    const fullName = String(candidate?.name ?? candidate?.displayName ?? '').trim();
    if (fullName) return fullName;
    const first = String(candidate?.first_name ?? candidate?.firstName ?? candidate?.givenName ?? '').trim();
    const last = String(candidate?.last_name ?? candidate?.lastName ?? candidate?.surname ?? '').trim();
    const derived = [first, last].filter(Boolean).join(' ').trim();
    return derived || getCandidateEmail(candidate) || 'User';
  };

  const hydrateFromCandidate = (candidate?: EmployeeAutocompleteResult | null, overrideEmail?: string) => {
    if (!candidate) return;
    const fullName = String(candidate.name ?? candidate.displayName ?? '').trim();
    const [derivedFirstName, ...restNameParts] = fullName.split(/\s+/).filter(Boolean);
    const derivedLastName = restNameParts.join(' ').trim();
    const nextFirstName = String(
      candidate.first_name ?? candidate.firstName ?? candidate.givenName ?? derivedFirstName ?? '',
    ).trim();
    const nextLastName = String(
      candidate.last_name ?? candidate.lastName ?? candidate.surname ?? derivedLastName ?? '',
    ).trim();
    const nextGpid = String(
      candidate.gpid ?? candidate.gpId ?? candidate.gp_id ?? candidate.employeeId ?? candidate.id ?? '',
    ).trim();
    const nextEmail =
      overrideEmail !== undefined
        ? overrideEmail
        : String(
            candidate.userEmail ?? candidate.email ?? candidate.mail ?? candidate.userPrincipalName ?? candidate.user_principal_name ?? '',
          ).trim();

    setForm((prev) => ({
      ...prev,
      firstName: nextFirstName,
      lastName: nextLastName,
      gpid: nextGpid,
      ...(nextEmail ? { email: nextEmail } : {}),
    }));
  };

  useEffect(() => {
    if (!form.department || showFullAccess) {
      setRoleOptionsData([]);
      setRolesLoading(false);
      return;
    }

    let cancelled = false;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const api = createApiClient();
        const response = await api.get('/admin/users/roles', {
          params: { department: form.department },
          _skipLocationContext: true,
        } as any);

        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        const normalized: DepartmentRoleOption[] = raw
          .map((item: any) => ({
            roleId: Number(item?.roleId),
            roleName: String(item?.roleName ?? '').trim(),
            scopeType: String(item?.scopeType ?? '').trim(),
          }))
          .filter((item: DepartmentRoleOption) => Number.isFinite(item.roleId) && item.roleName.length > 0);

        if (!cancelled) {
          setRoleOptionsData(normalized);
        }
      } catch {
        if (!cancelled) {
          setRoleOptionsData([]);
        }
      } finally {
        if (!cancelled) {
          setRolesLoading(false);
        }
      }
    };

    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [form.department, showFullAccess]);

  useEffect(() => {
    if (!form.role || !form.organization) {
      setFetchedLocationTree([]);
      setLocationsLoading(false);
      return;
    }

    let cancelled = false;
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const api = createApiClient();
        const response = await api.get('/admin/locations', {
          params: { organizationType: form.organization, page: 0, pageSize: 2000 },
          _skipLocationContext: true,
        } as any);

        const rows = extractLocationRows(response);
        const regionMap = new Map<string, Map<string, Set<string>>>();

        rows.forEach((loc: any) => {
          const region = String(loc.region ?? 'Unknown').trim() || 'Unknown';
          const market = String(loc.market ?? 'Unknown').trim() || 'Unknown';
          const site = String(loc.locName ?? loc.name ?? '').trim();

          if (!regionMap.has(region)) regionMap.set(region, new Map());
          const marketMap = regionMap.get(region)!;
          if (!marketMap.has(market)) marketMap.set(market, new Set());
          if (site) marketMap.get(market)!.add(site);
        });

        const tree: LocationRegion[] = Array.from(regionMap.entries()).map(([region, marketMap]) => ({
          region,
          siteCount: Array.from(marketMap.values()).reduce((sum, sites) => sum + sites.size, 0),
          markets: Array.from(marketMap.entries()).map(([market, sites]) => ({
            market,
            siteCount: sites.size,
            sites: Array.from(sites).map((name) => ({ name })),
          })),
        }));

        if (!cancelled) {
          setFetchedLocationTree(tree);
        }
      } catch {
        if (!cancelled) {
          setFetchedLocationTree([]);
        }
      } finally {
        if (!cancelled) {
          setLocationsLoading(false);
        }
      }
    };

    fetchLocations();
    return () => {
      cancelled = true;
    };
  }, [form.organization, form.role]);

  useEffect(() => {
    const query = form.email.trim();
    if (!query) {
      setEmailOptions([]);
      setEmailSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setEmailSuggestionsLoading(true);
      try {
        const api = createApiClient();
        const peopleResponse = await api.get('/admin/user-basic-access/search', {
          params: { key: query },
          _skipLocationContext: true,
        } as any);

        const options = extractAutocompleteOptions(peopleResponse?.data);

        if (!cancelled) {
          // Show search results immediately
          setEmailOptions(options);

          // Fire autocomplete in the background to enrich with firstName / lastName.
          // This is intentionally non-blocking: if it fails the dropdown still works.
          if (options.length > 0) {
            (async () => {
              try {
                const acApi = createApiClient();
                const acRes = await acApi.get('/common/employees/autocomplete', {
                  params: { key: query },
                  _skipLocationContext: true,
                } as any);
                if (cancelled) return;
                const acItems = extractAutocompleteOptions(acRes?.data);
                if (acItems.length === 0) return;
                const byEmail = new Map<string, EmployeeAutocompleteResult>();
                acItems.forEach((item: EmployeeAutocompleteResult) => {
                  const em = String(
                    item.email ?? item.mail ?? item.userPrincipalName ?? item.user_principal_name ?? '',
                  ).trim().toLowerCase();
                  if (em) byEmail.set(em, item);
                });
                if (cancelled) return;
                setEmailOptions((prev) =>
                  prev.map((result) => {
                    const re = String(
                      result.userEmail ?? result.email ?? result.mail ?? result.userPrincipalName ?? result.user_principal_name ?? '',
                    ).trim().toLowerCase();
                    const match = re ? byEmail.get(re) : undefined;
                    if (!match) return result;
                    return {
                      ...result,
                      firstName: result.firstName ?? result.first_name ?? result.givenName ?? match.firstName ?? match.first_name ?? match.givenName ?? null,
                      lastName: result.lastName ?? result.last_name ?? result.surname ?? match.lastName ?? match.last_name ?? match.surname ?? null,
                      first_name: result.first_name ?? match.first_name ?? null,
                      last_name: result.last_name ?? match.last_name ?? null,
                      name: result.name ?? match.name ?? match.displayName ?? null,
                      displayName: result.displayName ?? match.displayName ?? match.name ?? null,
                    };
                  }),
                );
              } catch {
                // Best-effort — autocomplete failure never breaks the search dropdown
              }
            })();
          }
        }
      } catch {
        if (!cancelled) {
          setEmailOptions([]);
        }
      } finally {
        if (!cancelled) {
          setEmailSuggestionsLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [form.email]);

  const validateExistingUserOnBlur = async (email: string) => {
    const hasIdentityData = (value: any): boolean => {
      if (!value || typeof value !== 'object') return false;

      const identityFields = [
        value.userId,
        value.id,
        value.user_id,
        value.email,
        value.gpid,
        value.gpId,
        value.firstName,
        value.lastName,
        value.username,
      ];

      return identityFields.some((field) => String(field ?? '').trim().length > 0);
    };

    const isExistingUserResponse = (payload: any): boolean => {
      const root = payload?.data ?? payload;
      if (root == null) return false;
      if (typeof root === 'boolean') return root;
      if (Array.isArray(root)) return root.length > 0;

      if (typeof root === 'object') {
        const candidates = [root, root.user, root.result, root.payload].filter(
          (item) => item && typeof item === 'object',
        );

        for (const candidate of candidates) {
          if (
            candidate.exists === true ||
            candidate.isExists === true ||
            candidate.isExist === true ||
            candidate.duplicate === true ||
            candidate.alreadyExists === true ||
            candidate.found === true
          ) {
            return true;
          }

          if (candidate.exists === false || candidate.isExists === false || candidate.found === false) {
            continue;
          }

          if (hasIdentityData(candidate)) {
            return true;
          }
        }
      }

      return false;
    };

    setEmailValidationLoading(true);
    try {
      const api = createApiClient();
      const existingUserResponse = await api.get('/admin/users/by-email', {
        params: { email },
        _skipLocationContext: true,
      } as any);

      const userExists = isExistingUserResponse(existingUserResponse.data);
      if (userExists) {
        setEmailError(`A user with email "${email}" already exists in the system.`);
        return false;
      } else {
        setEmailError(null);
        return true;
      }
    } catch (lookupError: any) {
      if (lookupError?.response?.status === 404) {
        setEmailError(null);
        return true;
      } else {
        setEmailError('Unable to validate this email right now.');
        return false;
      }
    } finally {
      setEmailValidationLoading(false);
    }
  };

  const handleEmailBlur = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
    }

    blurTimerRef.current = setTimeout(async () => {
      setShowEmailResults(false);
      const email = form.email.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError(null);
        return;
      }

      const matchedBySelection = selectedEmailOption && getCandidateEmail(selectedEmailOption) === email
        ? selectedEmailOption
        : null;
      const matchedByOptions = emailOptions.find((candidate) => getCandidateEmail(candidate) === email) ?? null;
      let candidate = matchedBySelection ?? matchedByOptions;

      if (!candidate) {
        try {
          const api = createApiClient();
          const peopleResponse = await api.get('/admin/user-basic-access/search', {
            params: { key: email },
            _skipLocationContext: true,
          } as any);
          const rawOptions = extractAutocompleteOptions(peopleResponse?.data);

          // Enrich with firstName / lastName via autocomplete — best-effort, non-blocking
          let options = rawOptions;
          try {
            const acApi = createApiClient();
            const acRes = await acApi.get('/common/employees/autocomplete', {
              params: { key: email },
              _skipLocationContext: true,
            } as any);
            const acItems = extractAutocompleteOptions(acRes?.data);
            if (acItems.length > 0) {
              const byEmail = new Map<string, EmployeeAutocompleteResult>();
              acItems.forEach((item: EmployeeAutocompleteResult) => {
                const em = String(
                  item.email ?? item.mail ?? item.userPrincipalName ?? item.user_principal_name ?? '',
                ).trim().toLowerCase();
                if (em) byEmail.set(em, item);
              });
              options = rawOptions.map((result) => {
                const re = String(
                  result.userEmail ?? result.email ?? result.mail ?? result.userPrincipalName ?? result.user_principal_name ?? '',
                ).trim().toLowerCase();
                const match = re ? byEmail.get(re) : undefined;
                if (!match) return result;
                return {
                  ...result,
                  firstName: result.firstName ?? result.first_name ?? result.givenName ?? match.firstName ?? match.first_name ?? match.givenName ?? null,
                  lastName: result.lastName ?? result.last_name ?? result.surname ?? match.lastName ?? match.last_name ?? match.surname ?? null,
                  first_name: result.first_name ?? match.first_name ?? null,
                  last_name: result.last_name ?? match.last_name ?? null,
                  name: result.name ?? match.name ?? match.displayName ?? null,
                  displayName: result.displayName ?? match.displayName ?? match.name ?? null,
                };
              });
            }
          } catch {
            // Autocomplete enrichment is best-effort — use raw search results on failure
          }

          candidate =
            options.find((option) => getCandidateEmail(option) === email) ??
            options[0] ??
            null;
        } catch {
          candidate = null;
        }
      }

      if (candidate) {
        setSelectedEmailOption(candidate);
      }
      hydrateFromCandidate(candidate);
      await validateExistingUserOnBlur(email);
    }, 140);
  };

  const handleEmailOptionSelect = (candidate: EmployeeAutocompleteResult) => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    console.log('[UserAddModal] selected candidate keys:', Object.keys(candidate));
    console.log('[UserAddModal] selected candidate values:', JSON.stringify(candidate));
    const nextEmail = String(
      candidate.userEmail ?? candidate.email ?? candidate.mail ?? candidate.userPrincipalName ?? candidate.user_principal_name ?? '',
    ).trim();
    console.log('[UserAddModal] resolved nextEmail:', nextEmail);
    setSelectedEmailOption(candidate);
    setEmailError(null);
    setShowEmailResults(false);
    // Single setForm call: sets firstName, lastName, gpid, AND email atomically
    hydrateFromCandidate(candidate, nextEmail);
    // Validate whether the selected email already exists ΓÇö run async but never block the UI
    if (nextEmail) {
      validateExistingUserOnBlur(nextEmail.toLowerCase()).catch(() => {
        // ignore network errors during selection; blur/submit will re-validate
      });
    }
  };

  const handleEmailSearchChange = (value: string) => {
    setEmailError(null);
    setSelectedEmailOption(null);
    setShowEmailResults(true);
    updateForm({ email: value, firstName: '', lastName: '', gpid: '' });
  };

  useEffect(() => {
    if (roleScope?.type === 'full') {
      setSelectedLocations(allSiteSelections);
    }
  }, [roleScope?.type, allSiteSelections]);

  useEffect(() => {
    if (!form.role) {
      setAccessibleModules([]);
      return;
    }

    const roleId = form.roleId;
    if (roleId == null) {
      setAccessibleModules([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setAccessibleLoading(true);
      try {
        const rows = await getAccessibleModules(roleId);
        if (!cancelled) {
          setAccessibleModules(Array.isArray(rows) ? (rows as AccessibleModuleItem[]) : []);
        }
      } catch {
        if (!cancelled) setAccessibleModules([]);
      } finally {
        if (!cancelled) setAccessibleLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [form.role, form.roleId]);

  const updateForm = (patch: Partial<UserAddPayload>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const setFieldPosition = (key: FieldKey) => (event: any) => {
    fieldPositionsRef.current[key] = event?.nativeEvent?.layout?.y ?? 0;
  };

  const scrollToField = (key: FieldKey) => {
    const y = fieldPositionsRef.current[key] ?? 0;
    formScrollRef.current?.scrollTo({ y: Math.max(y - 12, 0), animated: true });
  };

  const getSubmitValidationErrors = () => {
    const errors: Partial<Record<FieldKey, string>> = {};

    if (!form.firstName.trim()) errors.firstName = 'First Name is required.';
    if (!form.lastName.trim()) errors.lastName = 'Last Name is required.';
    if (!form.email.trim()) errors.email = 'Email is required.';
    if (!form.gpid.trim()) errors.gpid = 'GPID is required.';
    if (form.gpid.trim() && !/^\d+$/.test(form.gpid.trim())) errors.gpid = 'GPID must contain only numbers.';
    if (form.gpid.trim() && form.gpid.trim().length !== 8) errors.gpid = 'GPID must be exactly 8 digits.';

    // Organization, Department, Role, and Locations are only required for Normal User
    if (!showFullAccess) {
      if (!form.organization.trim()) errors.organization = 'Organization is required.';
      if (!form.department.trim()) errors.department = 'Department is required.';
      if (!form.role.trim() || form.roleId == null) errors.role = 'Role is required.';
      if (roleScope && roleScope.type !== 'full' && selectedLocations.length === 0) {
        errors.locations = 'Location selection is required for this role.';
      }
    }

    if (emailError) {
      errors.email = emailError;
    }

    return errors;
  };

  const handleOrganizationChange = (value: string) => {
    updateForm({ organization: value, department: '', role: '' });
    setFieldErrors((prev) => ({ ...prev, organization: undefined, department: undefined, role: undefined, locations: undefined }));
    setSelectedLocations([]);
    setAccessibleModules([]);
  };

  const handleDepartmentChange = (value: string) => {
    updateForm({ department: value, role: '', roleId: null });
    setFieldErrors((prev) => ({ ...prev, department: undefined, role: undefined, locations: undefined }));
    setSelectedLocations([]);
    setAccessibleModules([]);
    setRoleOptionsData([]);
  };

  const handleRoleChange = (value: string) => {
    const matched = roleOptionsData.find((role) => role.roleName === value);
    updateForm({ role: value, roleId: matched?.roleId ?? null });
    setFieldErrors((prev) => ({ ...prev, role: undefined, locations: undefined }));
    setSelectedLocations([]);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setForm(initialForm);
    setAccessTypeTier('Field');
    setSelectedLocations([]);
    setAccessibleModules([]);
    setAccessibleLoading(false);
    setLocationsLoading(false);
    setFetchedLocationTree([]);
    setIsSubmitting(false);
    setEmailError(null);
    setEmailSuggestionsLoading(false);
    setEmailValidationLoading(false);
    setEmailOptions([]);
    setSelectedEmailOption(null);
    setShowEmailResults(false);
    setFieldErrors({});
    onClose();
  };

  const canSubmit = useMemo(() => {
    const hasPersonFields =
      form.firstName.trim() &&
      form.lastName.trim() &&
      form.email.trim() &&
      form.gpid.trim();

    if (!hasPersonFields) return false;
    if (emailError || emailValidationLoading) return false;

    // Support tier needs a selected role; Normal User needs org, dept, role, and locations (if applicable)
    if (showFullAccess) return Boolean(form.accessType.trim());
    const hasAccessFields =
      form.organization.trim() &&
      form.department.trim() &&
      form.role.trim() &&
      form.roleId != null;

    if (!hasAccessFields) return false;
    if (!roleScope || roleScope.type === 'full') return true;
    return selectedLocations.length > 0;
  }, [emailError, emailValidationLoading, form, roleScope, selectedLocations, showFullAccess]);

  const handleSubmit = async () => {
    if (isSubmitting || emailValidationLoading) return;

    const normalizedEmail = form.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (normalizedEmail && !emailRegex.test(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
    }

    const nextErrors = getSubmitValidationErrors();
    if (normalizedEmail && !emailRegex.test(normalizedEmail)) {
      nextErrors.email = 'Please enter a valid email address.';
    }
    setFieldErrors(nextErrors);

    const hasErrors = Object.values(nextErrors).some(Boolean);
    if (hasErrors) {
      const invalidOrderBottomFirst: FieldKey[] = [
        'locations',
        'role',
        'department',
        'organization',
        'gpid',
        'email',
        'lastName',
        'firstName',
      ];
      const targetField = invalidOrderBottomFirst.find((field) => nextErrors[field]);
      if (targetField) {
        scrollToField(targetField);
      }
      return;
    }

    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    const isEmailUnique = await validateExistingUserOnBlur(normalizedEmail);
    if (!isEmailUnique) {
      setFieldErrors((prev) => ({ ...prev, email: `A user with email "${normalizedEmail}" already exists in the system.` }));
      scrollToField('email');
      return;
    }

    setFieldErrors({});

    if (!onSubmit) {
      handleClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit({
        ...form,
        selectedLocations,
      });
      if (result !== false) {
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminModal
      visible={visible}
      title="Add New User"
      onClose={handleClose}
      width={860}
      footer={(
        <>
          <AdminButton label="Cancel" variant="ghost" onPress={handleClose} disabled={isSubmitting} />
          <AdminButton
            label="Add User"
            variant="primary"
            onPress={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
          />
        </>
      )}
    >
      <ScrollView
        ref={formScrollRef}
        style={styles.formScroll}
        contentContainerStyle={styles.formScrollContent}
        showsVerticalScrollIndicator
      >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Access Request Details</Text>

        <View style={styles.row}>
          <View style={styles.field} onLayout={setFieldPosition('firstName')}>
            <AdminFormField label="First Name" required error={fieldErrors.firstName}>
              <AdminTextInput
                value={form.firstName}
                onChangeText={(value) => {
                  setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
                  updateForm({ firstName: value });
                }}
                placeholder="e.g. Jorge"
              />
            </AdminFormField>
          </View>
          <View style={styles.field} onLayout={setFieldPosition('lastName')}>
            <AdminFormField label="Last Name" required error={fieldErrors.lastName}>
              <AdminTextInput
                value={form.lastName}
                onChangeText={(value) => {
                  setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
                  updateForm({ lastName: value });
                }}
                placeholder="e.g. Jara"
              />
            </AdminFormField>
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.field} onLayout={setFieldPosition('email')}>
            <AdminFormField label="Email" required error={fieldErrors.email ?? emailError ?? undefined}>
              <View>
                <AdminTextInput
                  value={form.email}
                  onChangeText={handleEmailSearchChange}
                  onFocus={() => {
                    if (form.email.trim()) {
                      setShowEmailResults(true);
                    }
                  }}
                  onBlur={handleEmailBlur}
                  placeholder="firstname.lastname@pepsico.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  hasError={Boolean(fieldErrors.email ?? emailError)}
                />

                {showEmailResults && form.email.trim().length > 0 ? (
                  emailSuggestionsLoading ? (
                    <View style={styles.peopleSearchBlock}>
                      <View style={styles.peopleNoMatchBox}>
                        <Text style={styles.peopleNoMatchText}>Searching...</Text>
                      </View>
                    </View>
                  ) : emailOptions.length === 0 ? (
                    <View style={styles.peopleSearchBlock}>
                      <View style={styles.peopleNoMatchBox}>
                        <Text style={styles.peopleNoMatchText}>No match.</Text>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.peopleSearchBlock}>
                      {emailOptions.slice(0, 10).map((candidate, index) => {
                        const email = getCandidateEmail(candidate);
                        return (
                          <Pressable
                            key={`${candidate.id ?? email}-${index}`}
                            style={({ pressed }) => [
                              styles.peopleSearchResultRow,
                              index === Math.min(emailOptions.length, 10) - 1 ? styles.peopleSearchResultRowLast : null,
                              pressed ? { backgroundColor: '#f3f7fd' } : null,
                            ]}
                            onPress={() => handleEmailOptionSelect(candidate)}
                            {...(Platform.OS === 'web' ? {
                              // Prevent the text input blur from firing before onPress on web.
                              // Without this, the dropdown unmounts during mousedown→blur (140 ms
                              // timer) before the click event completes, so onPress never fires.
                              onStartShouldSetResponder: () => true,
                              onMouseDown: (e: any) => { e.preventDefault(); },
                            } as any : {})}
                          >
                            <View style={styles.peopleAvatarCircle}>
                              <Text style={styles.peopleAvatarText}>{getInitials(getCandidateName(candidate))}</Text>
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={styles.peopleResultName}>{getCandidateName(candidate)}</Text>
                              <Text style={styles.peopleResultMeta}>{formatCandidateMeta(candidate)}</Text>
                            </View>
                          </Pressable>
                        );
                      })}
                    </View>
                  )
                ) : null}

                {selectedEmailOption ? (
                  <View style={styles.peopleSelectedCard}>
                    <View style={styles.peopleAvatarCircle}>
                      <Text style={styles.peopleAvatarText}>{getInitials(getCandidateName(selectedEmailOption))}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.peopleCardName}>{getCandidateName(selectedEmailOption)}</Text>
                      <Text style={styles.peopleCardMeta}>{formatCandidateMeta(selectedEmailOption)}</Text>
                    </View>
                    <Pressable
                      onPress={() => {
                        setSelectedEmailOption(null);
                        setEmailOptions([]);
                        setEmailError(null);
                        updateForm({ email: '', firstName: '', lastName: '', gpid: '' });
                      }}
                      style={styles.peopleCardClose}
                      hitSlop={6}
                    >
                      <Text style={styles.peopleCardCloseText}>x</Text>
                    </Pressable>
                  </View>
                ) : null}
              </View>
            </AdminFormField>
          </View>
          <View style={styles.field} onLayout={setFieldPosition('gpid')}>
            <AdminFormField label="GPID" required error={fieldErrors.gpid}>
              <AdminTextInput
                value={form.gpid}
                onChangeText={(value) => {
                  // Only allow digits and limit to 8 characters
                  const digitsOnly = value.replace(/[^0-9]/g, '').slice(0, 8);
                  const error = digitsOnly.length > 0 && digitsOnly.length !== 8 ? 'GPID must be exactly 8 digits' : undefined;
                  setFieldErrors((prev) => ({ ...prev, gpid: error }));
                  updateForm({ gpid: digitsOnly });
                }}
                placeholder="e.g. 09497611"
                keyboardType="numeric"
                maxLength={8}
                hasError={Boolean(fieldErrors.gpid)}
              />
            </AdminFormField>
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.field}>
            <AdminFormField label="Access Type Tier" required>
              <UserFormSelect
                value={accessTypeTier}
                options={[...ACCESS_TYPE_TIERS]}
                placeholder="Select access type tier..."
                onChange={(value) => {
                  const tier = value as typeof ACCESS_TYPE_TIERS[number];
                  setAccessTypeTier(tier);
                  updateForm({
                    accessType: tier === 'Support' ? ('' as AccessType) : 'Normal User',
                    organization: '', department: '', role: '', roleId: null,
                  });
                  setSelectedLocations([]);
                  setAccessibleModules([]);
                  setRoleOptionsData([]);
                  setFetchedLocationTree([]);
                  setEmailError(null);
                  setFieldErrors((prev) => ({ ...prev, organization: undefined, department: undefined, role: undefined, locations: undefined }));
                }}
              />
            </AdminFormField>
            <Text style={styles.fieldHint}>
              Field and 3PL/Outside Labor use Organization, Department, and Role below. Support roles get access to everything.
            </Text>
          </View>
          <View style={styles.field}>
            {accessTypeTier === 'Support' ? (
              <AdminFormField label="Role" required>
                <UserFormSelect
                  value={form.accessType}
                  options={supportTierRoles.map((r) => r.roleName)}
                  placeholder={supportTierRolesLoading ? 'Loading roles...' : 'Select role...'}
                  onChange={(value) => {
                    // Also set role/roleId so the Accessible Modules preview resolves correctly.
                    const matched = supportTierRoles.find((r) => r.roleName === value);
                    updateForm({ accessType: value as AccessType, role: value, roleId: matched?.roleId ?? null });
                  }}
                />
              </AdminFormField>
            ) : null}
          </View>
        </View>

        {/* Full-access banner for Support-tier roles */}
        {showFullAccess && form.accessType ? (
          <View style={[styles.fullAccessBanner, { marginBottom: 12 }]}>
            <MaterialIcons name="shield" size={16} color={AdminColors.green600} />
            <Text style={styles.fullAccessText}>
              <Text style={{ fontWeight: '700' }}>{form.accessType}</Text> access grants access to all locations globally.
            </Text>
          </View>
        ) : null}

        {/* Normal User: Org, Dept, Role, and Locations */}
        {!showFullAccess ? (
          <>
            <View style={styles.row}>
              <View style={styles.field} onLayout={setFieldPosition('organization')}>
                <AdminFormField label="Organization" required error={fieldErrors.organization}>
                  <UserFormSelect
                    value={form.organization}
                    options={Object.keys(DEPARTMENT_OPTIONS)}
                    placeholder="Select organization..."
                    onChange={handleOrganizationChange}
                  />
                </AdminFormField>
                <Text style={styles.fieldHint}>Scopes which locations are available downstream.</Text>
              </View>
              <View style={styles.field} onLayout={setFieldPosition('department')}>
                <AdminFormField label="Department" required error={fieldErrors.department}>
                  <UserFormSelect
                    value={form.department}
                    options={departmentOptions}
                    placeholder={form.organization ? 'Select a department...' : 'Select an organization first'}
                    onChange={handleDepartmentChange}
                    disabled={!form.organization}
                  />
                </AdminFormField>
                <Text style={styles.fieldHint}>Determines the available roles.</Text>
              </View>
            </View>

            <View style={styles.row}>
              <View style={styles.field} onLayout={setFieldPosition('role')}>
                <AdminFormField label="Role" required error={fieldErrors.role}>
                  <UserFormSelect
                    value={form.role}
                    options={roleOptions}
                    placeholder={
                      !form.department
                        ? 'Select a department first'
                        : rolesLoading
                        ? 'Loading roles...'
                        : roleOptions.length
                        ? 'Select a role...'
                        : 'No roles available for this department'
                    }
                    onChange={handleRoleChange}
                    disabled={!form.department || rolesLoading || roleOptions.length === 0}
                  />
                </AdminFormField>
                {roleScope ? (
                  <Text style={styles.scopeLine}>
                    Role scope: <Text style={styles.scopeAccent}>{roleScope.accessLabel}</Text> - {roleScope.scopeLabel}
                  </Text>
                ) : null}
              </View>
              <View style={styles.field} />
            </View>

            {roleScope ? (
              <View style={styles.locationSection} onLayout={setFieldPosition('locations')}>
                {locationsLoading ? (
                  <View style={styles.loadingState}>
                    <Text style={styles.loadingStateText}>Loading locations...</Text>
                  </View>
                ) : (
                  <AccessRequestLocationPicker
                    pickerType={roleScope.type === 'full' ? 'site' : pickerType}
                    organization={form.organization}
                    selectedItems={roleScope.type === 'full' ? allSiteSelections : selectedLocations}
                    onSelectionChange={setSelectedLocations}
                    locationTree={activeLocationTree}
                    selectionLocked={roleScope.type === 'full'}
                  />
                )}
                {fieldErrors.locations ? (
                  <Text style={styles.locationErrorText}>{fieldErrors.locations}</Text>
                ) : null}
                {roleScope.type === 'full' ? (
                  <View style={styles.fullAccessBanner}>
                    <MaterialIcons name="verified-user" size={16} color={AdminColors.green600} />
                    <Text style={styles.fullAccessText}>
                      All locations selected - {activeLocationTree.length} regions - {totalMarketCount} markets - {allSiteSelections.length} sites.
                    </Text>
                  </View>
                ) : null}
              </View>
            ) : null}
          </>
        ) : null}

        <View style={styles.modulesCard}>
          <View style={styles.modulesCardHeader}>
            <MaterialIcons name="info-outline" size={16} color="#1d4ed8" />
            <View style={{ flex: 1, marginLeft: 8 }}>
              <Text style={styles.modulesCardTitle}>What does this access provide?</Text>
              <Text style={styles.modulesCardSubtitle}>From the Config module's role privileges for the selected site(s)</Text>
            </View>
          </View>

          <View style={styles.modulesSameAccessBadge}>
            <Text style={styles.modulesSameAccessBadgeText}>
              {isFullScope ? 'DEFAULT CONFIGURATION' : 'SAME ACCESS AT ALL SELECTED SITES'}
            </Text>
          </View>

          <Text style={styles.modulesAccessLabel}>
            {isFullScope
              ? `Based on the default ${form.role || 'role'} configuration`
              : "After this change, you'll have access to:"}
          </Text>

          <View style={styles.modulesPillsRow}>
            {accessibleLoading ? (
              <Text style={styles.modulesAccessLabel}>Loading modules...</Text>
            ) : fullAccessModules.length > 0 ? (
              fullAccessModules.map((mod) => (
                <View key={mod.accessId} style={styles.modulePill}>
                  <Text style={styles.modulePillText}>{mod.moduleName}</Text>
                </View>
              ))
            ) : visibleAccessibleModules.length === 0 ? (
              <Text style={styles.modulesAccessLabel}>Select a role to view accessible modules.</Text>
            ) : null}
          </View>

          {!accessibleLoading && viewOnlyModules.length > 0 ? (
            <>
              <Text style={[styles.modulesAccessLabel, { marginTop: 12 }]}>
                With this role, the user will have <Text style={{ fontWeight: '700' }}>VIEW ONLY</Text> access to:
              </Text>
              <View style={styles.modulesPillsRow}>
                {viewOnlyModules.map((mod) => (
                  <View key={mod.accessId} style={styles.modulePill}>
                    <Text style={styles.modulePillText}>{mod.moduleName}</Text>
                  </View>
                ))}
              </View>
            </>
          ) : null}
        </View>
      </View>
      </ScrollView>
    </AdminModal>
  );
};

const styles = StyleSheet.create({
  formScroll: {
    maxHeight: 620,
  },
  formScrollContent: {
    paddingBottom: 8,
  },
  section: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.gray500,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 4,
  },
  field: {
    flex: 1,
  },
  peopleSearchBlock: {
    marginTop: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d0d0d0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  peopleNoMatchBox: {
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  peopleNoMatchText: {
    fontSize: 13,
    color: '#6b7280',
    fontFamily: FontFamily.regular,
  },
  peopleSearchResultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    gap: 10,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  peopleSearchResultRowLast: {
    borderBottomWidth: 0,
  },
  peopleResultName: {
    fontSize: 13,
    color: '#2d2d2d',
    fontFamily: FontFamily.bold,
  },
  peopleResultMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 1,
    fontFamily: FontFamily.regular,
  },
  peopleSelectedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f4fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d0dff0',
    paddingHorizontal: 12,
    paddingVertical: 9,
    marginTop: 10,
    gap: 10,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  peopleAvatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#005cbc',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  peopleAvatarText: {
    fontSize: 13,
    color: '#ffffff',
    fontFamily: FontFamily.bold,
  },
  peopleCardName: {
    fontSize: 13,
    lineHeight: 18,
    color: '#1a1a1a',
    fontFamily: FontFamily.bold,
  },
  peopleCardMeta: {
    fontSize: 11,
    color: '#585858',
    marginTop: 1,
    fontFamily: FontFamily.regular,
  },
  peopleCardClose: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginLeft: 4,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  peopleCardCloseText: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 20,
    textAlign: 'center',
    fontFamily: FontFamily.medium,
  },
  fieldHint: {
    marginTop: -6,
    marginBottom: 8,
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  scopeLine: {
    marginTop: -6,
    marginBottom: 8,
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.gray500,
    fontFamily: FontFamily.regular,
  },
  scopeAccent: {
    color: AdminColors.blue700,
    fontFamily: FontFamily.bold,
  },
  locationSection: {
    marginTop: 2,
    marginBottom: 10,
  },
  loadingState: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  loadingStateText: {
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.blue700,
    fontFamily: FontFamily.medium,
  },
  locationErrorText: {
    marginTop: 6,
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textError,
    fontFamily: FontFamily.regular,
  },
  fullAccessBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#BDE6CA',
    backgroundColor: '#E5F7EA',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  fullAccessText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    color: '#166A2C',
    fontFamily: FontFamily.medium,
  },
  modulesCard: {
    borderWidth: 1,
    borderColor: '#bfd5f3',
    backgroundColor: '#f3f8ff',
    borderRadius: 10,
    padding: 14,
    marginTop: 6,
  },
  modulesCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  modulesCardTitle: {
    fontSize: 14,
    lineHeight: 18,
    color: '#173f91',
    fontFamily: FontFamily.bold,
  },
  modulesCardSubtitle: {
    fontSize: 12,
    lineHeight: 16,
    color: '#2563eb',
    fontFamily: FontFamily.regular,
    marginTop: 2,
  },
  modulesSameAccessBadge: {
    alignSelf: 'flex-start',
    marginTop: 10,
    borderRadius: 999,
    backgroundColor: '#d5f5dc',
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  modulesSameAccessBadgeText: {
    fontSize: 11,
    lineHeight: 14,
    color: '#0f7a36',
    fontFamily: FontFamily.bold,
  },
  modulesAccessLabel: {
    marginTop: 10,
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  modulesPillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  modulePill: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#d8e4fb',
    backgroundColor: '#ffffff',
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  modulePillText: {
    fontSize: 11,
    lineHeight: 14,
    color: '#1f3f91',
    fontFamily: FontFamily.medium,
  },
});

export default UserAddModal;
